/* eslint-disable  */
import CardAccordion from 'components/atoms/CardAccordion';
import ChartActivityStatus from 'components/molecules/ChartActivityStatus';
import ChartDailyBulletin from 'components/molecules/ChartDailyBulletin';
import { IChartData } from 'components/molecules/ChartDailyBulletin/types';
import ChartDailyBulletinBigCorrectives from 'components/molecules/ChartDailyBulletinBigCorrectives';
import { IChartMajorStatusData } from 'components/molecules/ChartDailyBulletinBigCorrectives/types';
import ChartDailyBulletinPreventiveCorrective from 'components/molecules/ChartDailyBulletinPreventiveCorrective';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { IDailyBulletinStatusChart } from 'interfaces/Chart';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  postDailyBulletinChart,
  postDailyBulletinMajorChart,
  postDailyBulletinPreventiveCorrectiveChart,
  postDailyBulletinStatusChart,
} from 'services/daily-bulletin';
import { colors } from 'styles/colors';
import { Box } from '@material-ui/core';
import useStyles from './style';

const ACTIVITY_BY_MAINTENENCETYPE_CHART = {
  total: 0,
  preventive: 0,
  preditive: 0,
  corrective: 0,
  bigCorrective: 0,
  others: 0,
};

const PREVENTIVE_CORRECTIVE_CHART = {
  total: 0,
  preventive: 0,
  corrective: 0,
};

const DailyBulletinChartsContainer = ({
  updatedAt,
}: {
  updatedAt: Date | null;
}) => {
  const [expandCard, setExpandCard] = useState(true);
  const classes = useStyles();

  const [
    installationFilterOptionsContext,
    dateFilterOptionsContext,
    supplierFilterOptionsContext,
  ] = [
    useSelectFilter('installation'),
    useSelectFilter('date'),
    useSelectFilter('supplier'),
  ];

  const allFilterContexts = [
    installationFilterOptionsContext,
    dateFilterOptionsContext,
    supplierFilterOptionsContext,
    updatedAt,
  ];

  const installationIds = installationFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const supplierExecutors = useMemo(
    () => supplierFilterOptionsContext.map(item => Number(item?.value)),
    [supplierFilterOptionsContext],
  );

  const dateSelected = useMemo(
    () => dateFilterOptionsContext[0].value,
    [dateFilterOptionsContext],
  );

  /** Gráfico de Status das Atividades */
  const queryDailyBulletinChart = useQuery({
    queryKey: ['queryDailyBulletinChartContext', allFilterContexts],
    initialData: ACTIVITY_BY_MAINTENENCETYPE_CHART,
    queryFn: async () => {
      const params = {
        date: `${dateSelected}`,
        installationIds,
        supplierExecutors,
      };

      const { data } = await postDailyBulletinChart(params);
      return data;
    },
    select: data => {
      const tempData = {
        ...ACTIVITY_BY_MAINTENENCETYPE_CHART,
        ...data,
      };

      const newData = [] as IChartData[];
      if ('preventive' in tempData)
        newData.push({
          maintenenceType: 'Preventivas',
          total: tempData.preventive,
          color: '#084d6e',
        });
      if ('preditive' in tempData)
        newData.push({
          maintenenceType: 'Preditivas',
          total: tempData.preditive,
          color: '#0077af',
        });
      if ('corrective' in tempData)
        newData.push({
          maintenenceType: 'Corretivas',
          total: tempData.corrective,
          color: '#87c2f1',
        });
      if ('bigCorrective' in tempData)
        newData.push({
          maintenenceType: 'Grandes Corretivas',
          total: tempData.bigCorrective,
          color: '#3980FF',
        });
      if ('others' in tempData)
        newData.push({
          maintenenceType: 'Outros',
          total: tempData.others,
          color: '#1536bf',
        });

      return { result: newData, total: data.total };
    },
    enabled: true,
  });

  const queryDailyBulletinPreventiveCorrectiveChart = useQuery({
    queryKey: [
      'queryDailyBulletinPreventiveCorrectiveChartContext',
      allFilterContexts,
    ],
    initialData: PREVENTIVE_CORRECTIVE_CHART,
    queryFn: async () => {
      const params = {
        date: `${dateSelected}`,
        installationIds,
        supplierExecutors,
      };

      const { data } = await postDailyBulletinPreventiveCorrectiveChart(params);
      return data;
    },
    select: data => {
      const tempData = {
        ...PREVENTIVE_CORRECTIVE_CHART,
        ...data,
      };

      const newData = [] as IChartData[];
      if ('preventive' in tempData)
        newData.push({
          maintenenceType: 'Preventivas',
          total: tempData.preventive,
          color: '#4365A8',
        });

      if ('corrective' in tempData)
        newData.push({
          maintenenceType: 'Corretivas',
          total: tempData.corrective,
          color: '#FF7979',
        });

      return { result: newData, total: data.total };
    },
    enabled: true,
  });

  const queryMajorCorrectives = useQuery({
    queryKey: ['queryDailyBulletinMajorContext', allFilterContexts],
    queryFn: async () => {
      const params = {
        date: `${dateSelected}`,
        installationIds,
        supplierExecutors,
      };

      const { data } = await postDailyBulletinMajorChart(params);

      return data;
    },
    select: data => {
      const newData = data.chartInfo.map(chartItem => {
        const extraInfo = chartItem.status.reduce((acc, cv) => {
          return {
            ...acc,
            [cv.description]: cv.total,
          } as { [key: string]: number };
        }, {});

        return {
          label: chartItem.justification,
          total: chartItem.status.reduce((acc, cv) => acc + cv.total, 0),
          ...extraInfo,
        } as IChartMajorStatusData;
      });

      return {
        result: newData.sort((a, b) => a.total - b.total),
        total: data.total,
      };
    },
    enabled: true,
  });

  const queryDailyBulletinStatusChart = useQuery<
    IDailyBulletinStatusChart,
    unknown
  >({
    queryKey: ['queryDailyBulletinStatusChartContext', allFilterContexts],
    queryFn: async () => {
      const params = {
        date: `${dateSelected}`,
        installationIds,
        supplierExecutors,
      };

      const { data } = await postDailyBulletinStatusChart(params);
      return data;
    },
    enabled: true,
  });
  /** Fim das variáveis do Gráfico de Status das Atividades */

  return (
    <div className={classes.container}>
      <div className={classes.overflowContainer}>
        <CardAccordion
          title="Atividades por Tipos de Manutenção"
          subtitle=""
          values={`Total: ${queryDailyBulletinChart.data?.total || 0}`}
          ModalContent={null}
          UpperRightIcon={null}
          backgroundColor={colors.cardAccordionBackgroundDefault}
          clickable={false}
          upperRightIconCallback={() => null}
          expandCard={expandCard}
          setExpandCard={setExpandCard}
          isLoading={queryDailyBulletinChart?.isLoading}
        >
          <Box>
            <ChartDailyBulletin
              data={queryDailyBulletinChart.data?.result || []}
              total={queryDailyBulletinChart.data?.total || 0}
            />

            {/* <CardAccordion
              title="Preventivas x Corretivas"
              subtitle=""
              values={`Total: ${
                queryDailyBulletinPreventiveCorrectiveChart.data?.total || 0
              }`}
              ModalContent={null}
              UpperRightIcon={null}
              backgroundColor={''}
              clickable={false}
              upperRightIconCallback={() => null}
              expandCard={expandCard}
              setExpandCard={setExpandCard}
              isLoading={queryDailyBulletinPreventiveCorrectiveChart?.isLoading}
            >

            </CardAccordion> */}

            <ChartDailyBulletinPreventiveCorrective
              data={
                queryDailyBulletinPreventiveCorrectiveChart.data?.result || []
              }
              total={
                queryDailyBulletinPreventiveCorrectiveChart.data?.total || 0
              }
            />
          </Box>
        </CardAccordion>

        {/* <CardAccordion
          title="Preventivas x Corretivas"
          subtitle=""
          values={`Total: ${
            queryDailyBulletinPreventiveCorrectiveChart.data?.total || 0
          }`}
          ModalContent={null}
          UpperRightIcon={null}
          backgroundColor={colors.cardAccordionBackgroundDefault}
          clickable={false}
          upperRightIconCallback={() => null}
          expandCard={expandCard}
          setExpandCard={setExpandCard}
          isLoading={queryDailyBulletinPreventiveCorrectiveChart?.isLoading}
        >
          <ChartDailyBulletinPreventiveCorrective
            data={
              queryDailyBulletinPreventiveCorrectiveChart.data?.result || []
            }
            total={queryDailyBulletinPreventiveCorrectiveChart.data?.total || 0}
          />
        </CardAccordion> */}

        <CardAccordion
          title="Status das Atividades"
          subtitle=""
          values={`Total: ${queryDailyBulletinStatusChart.data?.total || 0}`}
          ModalContent={null}
          UpperRightIcon={null}
          backgroundColor={colors.cardAccordionBackgroundDefault}
          clickable={false}
          upperRightIconCallback={() => null}
          expandCard={expandCard}
          setExpandCard={setExpandCard}
          isLoading={queryDailyBulletinStatusChart?.isLoading}
        >
          <ChartActivityStatus
            data={queryDailyBulletinStatusChart.data?.chartInfo || []}
            total={queryDailyBulletinStatusChart.data?.total || 0}
          />
        </CardAccordion>

        <CardAccordion
          title="GC - Pausadas e/ou Não Iniciadas"
          subtitle=""
          ModalContent={null}
          UpperRightIcon={null}
          backgroundColor={colors.cardAccordionBackgroundDefault}
          clickable={false}
          upperRightIconCallback={() => null}
          values={`Total: ${queryMajorCorrectives.data?.total || 0}`}
          expandCard={expandCard}
          setExpandCard={setExpandCard}
          isLoading={
            queryMajorCorrectives?.isLoading ||
            queryMajorCorrectives?.isFetching
          }
        >
          <ChartDailyBulletinBigCorrectives
            data={queryMajorCorrectives.data?.result || []}
            total={queryMajorCorrectives.data?.total || 0}
          />
        </CardAccordion>
      </div>
    </div>
  );
};

export default DailyBulletinChartsContainer;
